import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { storeConstants } from "../../constants";
import { useColumnSizingPersistence } from "./useColumnSizingPersistence";

export const useResizableTableColumns = ({
  columns = [],
  tableId,
  resizableColumns = true,
  user,
  store,
  updateUserInformation,
  headerCellSelector = "th",
}) => {
  const [columnWidths, setColumnWidths] = useState({});
  const [isSizingInitialized, setIsSizingInitialized] = useState(false);
  const activeResizeRef = useRef(null);

  const minColumnWidth = storeConstants.GRID_MIN_COLUMN_WIDTH;
  const maxColumnWidth = storeConstants.GRID_MAX_COLUMN_WIDTH;

  const savedColumnSizing = tableId
    ? user?.userPreferences?.tables?.[tableId]?.columnSizing
    : null;

  useColumnSizingPersistence({
    tableId,
    columnSizing: columnWidths,
    user,
    store,
    updateUserInformation,
    enabled: resizableColumns && isSizingInitialized,
  });

  const getColumnKey = useCallback((column, index) => {
    return String(column?.id);
  }, []);

  const handleResizeMove = useCallback(
    (event) => {
      const activeResize = activeResizeRef.current;

      if (!activeResize) {
        return;
      }

      const newWidth = Math.min(
        maxColumnWidth,
        Math.max(
          minColumnWidth,
          Math.round(
            activeResize.startWidth + (event.clientX - activeResize.startX)
          )
        )
      );

      setColumnWidths((previousColumnWidths) => {
        if (previousColumnWidths[activeResize.columnKey] === newWidth) {
          return previousColumnWidths;
        }

        return {
          ...previousColumnWidths,
          [activeResize.columnKey]: newWidth,
        };
      });
    },
    [maxColumnWidth, minColumnWidth]
  );

  const handleResizeStop = useCallback(() => {
    activeResizeRef.current = null;
    window.removeEventListener("mousemove", handleResizeMove);
    window.removeEventListener("mouseup", handleResizeStop);
  }, [handleResizeMove]);

  const handleResizeStart = useCallback(
    (event, columnKey) => {
      event.preventDefault();
      event.stopPropagation();

      const headerCell = event.currentTarget.closest(headerCellSelector);
      const headerWidth = headerCell?.getBoundingClientRect?.()?.width;
      const initialWidth = Math.min(
        maxColumnWidth,
        Math.max(
          minColumnWidth,
          columnWidths[columnKey] || Math.round(headerWidth || 140)
        )
      );

      setColumnWidths((previousColumnWidths) => ({
        ...previousColumnWidths,
        [columnKey]: initialWidth,
      }));

      activeResizeRef.current = {
        columnKey,
        startX: event.clientX,
        startWidth: initialWidth,
      };

      window.addEventListener("mousemove", handleResizeMove);
      window.addEventListener("mouseup", handleResizeStop);
    },
    [
      columnWidths,
      handleResizeMove,
      handleResizeStop,
      headerCellSelector,
      maxColumnWidth,
      minColumnWidth,
    ]
  );

  const getColumnWidthStyle = useCallback(
    (columnKey, { minWidth, maxWidth } = {}) => {
      const resolvedMinWidth = minWidth || `${minColumnWidth}px`;
      const resolvedMaxWidth = maxWidth || `${maxColumnWidth}px`;
      const resizedWidth = columnWidths[columnKey];
      const clampedResizedWidth = resizedWidth
        ? Math.min(maxColumnWidth, Math.max(minColumnWidth, resizedWidth))
        : null;

      if (!clampedResizedWidth) {
        return {
          minWidth: resolvedMinWidth,
          maxWidth: resolvedMaxWidth,
        };
      }

      const width = `${clampedResizedWidth}px`;

      return {
        width,
        minWidth: width,
        maxWidth: width,
      };
    },
    [columnWidths, maxColumnWidth, minColumnWidth]
  );

  const renderResizableHeader = useCallback(
    (headerContent, columnKey) => {
      if (!resizableColumns) {
        return headerContent;
      }

      return (
        <div className="rdt-resizable-header">
          <div className="rdt-resizable-header-content">{headerContent}</div>
          <div
            className="rdt-column-resizer"
            onMouseDown={(event) => handleResizeStart(event, columnKey)}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onDoubleClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          />
        </div>
      );
    },
    [handleResizeStart, resizableColumns]
  );

  const formatColumns = useMemo(
    () =>
      columns.map((column, index) => ({
        ...column,
        __columnKey: getColumnKey(column, index),
      })),
    [columns, getColumnKey]
  );

  useEffect(() => {
    setIsSizingInitialized(false);

    if (savedColumnSizing && typeof savedColumnSizing === "object") {
      setColumnWidths(savedColumnSizing);
    } else {
      setColumnWidths({});
    }

    setIsSizingInitialized(true);
  }, [tableId, savedColumnSizing]);

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleResizeMove);
      window.removeEventListener("mouseup", handleResizeStop);
    };
  }, [handleResizeMove, handleResizeStop]);

  return {
    formatColumns,
    getColumnKey,
    getColumnWidthStyle,
    renderResizableHeader,
  };
};
